<!-- src/components/forms/completed/InvestigationCompleted.vue -->
<template>
  <div class="bg-white p-2 sm:p-4 md:p-8 space-y-12 animate-fadeIn relative text-left pb-24 min-h-screen">
    
    <!-- 1. PROTOCOL NAVIGATION (Skip to Summary) -->
    <div class="sticky top-0 z-30 py-4 no-print">
      <div class="bg-[#0A1630]/90 backdrop-blur-md border border-white/10 p-4 rounded-3xl shadow-2xl flex items-center justify-between gap-6">
         <div class="flex items-center gap-3 pl-4">
            <div class="w-2 h-2 bg-[#20FCF1] rounded-full animate-pulse"></div>
            <span class="text-[9px] font-black text-white uppercase tracking-[0.3em]">Protocol Navigation Active</span>
         </div>
         <div class="flex items-center gap-3">
            <button 
              @click="scrollToSection('executive-summary')" 
              class="bg-[#20FCF1] text-[#0A1630] px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all flex items-center gap-2 shadow-lg"
            >
                <ArrowDownCircleIcon class="h-4 w-4" />
                <span>Skip to Summary</span>
            </button>
            <button 
              v-if="hasAppraisalAccess"
              @click="scrollToSection('management-appraisal')" 
              class="bg-white/10 text-white border border-white/20 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all flex items-center gap-2"
            >
                <ShieldCheckIcon class="h-4 w-4 text-[#20FCF1]" />
                <span>Stage 2 Appraisal</span>
            </button>
         </div>
      </div>
    </div>

    <!-- 2. AUDIT HEADER: TRIPLE HANDSHAKE -->
    <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-xl overflow-hidden">
      <div class="bg-[#0A1630] p-8 md:p-10 border-b-4 border-[#20FCF1] flex flex-col lg:flex-row justify-between items-center gap-8 text-left">
        <div class="flex items-center gap-6">
          <div class="bg-[#20FCF1]/10 p-4 rounded-[24px]">
            <LinkIcon class="h-8 w-8 text-[#20FCF1]" />
          </div>
          <div>
            <p class="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-1">Investigation Handshake</p>
            <h3 class="text-2xl font-black text-white uppercase tracking-tight leading-none">
              {{ submissionData.subjectUser || 'Personnel' }}
            </h3>
            <div class="flex items-center gap-3 mt-3">
              <span class="text-[10px] font-black text-[#20FCF1] bg-[#20FCF1]/10 px-3 py-1 rounded-full uppercase">{{ submissionData.accidentDate }}</span>
              <span class="text-white/20">|</span>
              <span class="text-[10px] font-bold text-white uppercase tracking-widest truncate max-w-[300px]">{{ submissionData.accidentLocation }}</span>
            </div>
          </div>
        </div>
        <div class="text-center lg:text-right shrink-0">
          <p class="text-[10px] font-black uppercase tracking-[0.3em] text-[#20FCF1] mb-2">Audit Registry Entry</p>
          <p class="text-4xl font-black text-white tracking-tighter leading-none">{{ submissionData.humanReadableId || submissionData.referenceNumber }}</p>
        </div>
      </div>
      
      <div class="p-8 bg-slate-50 flex flex-wrap gap-4 items-center border-t border-slate-100 text-left">
        <div class="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
          <span class="text-[9px] font-black text-slate-400 uppercase">Linked Accident:</span>
          <span class="text-[10px] font-black text-[#0A1630]">{{ submissionData.linkedAccidentRef || 'N/A' }}</span>
        </div>
        <div v-if="submissionData.linkedWitnessRef" class="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
          <span class="text-[9px] font-black text-slate-400 uppercase">Linked Witness:</span>
          <span class="text-[10px] font-black text-[#0A1630]">{{ submissionData.linkedWitnessRef }}</span>
        </div>
      </div>
    </div>

    <!-- 3. INCIDENT NARRATIVE (ALIGNED WITH ACCIDENT REGISTRY) -->
    <section class="space-y-6">
      <div class="flex items-center gap-3 text-left">
        <!-- 🛡️ ICON SWAP APPLIED FOR BUILD RESOLUTION -->
        <div class="bg-[#0A1630] p-2 rounded-lg shadow-sm">
          <ChatBubbleLeftRightIcon class="h-5 w-5 text-[#20FCF1]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Narrative Audit</h3>
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 text-left">
        <div v-for="field in narrativeFields" :key="field.key" class="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm flex flex-col">
           <h4 class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-50 pb-2">{{ field.label }}</h4>
           <p class="text-sm text-slate-600 leading-relaxed font-medium italic grow whitespace-pre-wrap">"{{ submissionData[field.key] || 'Not provided.' }}"</p>
        </div>
      </div>
    </section>

    <!-- 4. CONTRIBUTORY VECTORS -->
    <section v-if="submissionData.selectedFactors?.length > 0" class="space-y-6">
      <div class="flex items-center gap-3 text-left">
        <div class="bg-[#20FCF1] p-2 rounded-lg shadow-sm">
          <AdjustmentsHorizontalIcon class="h-5 w-5 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Identified Contributory Factors</h3>
      </div>
      <div class="flex flex-wrap gap-3 p-10 bg-slate-50 rounded-[40px] border border-slate-100 shadow-inner text-left">
        <div v-for="(factor, idx) in submissionData.selectedFactors" :key="idx" class="flex items-center gap-3 bg-white px-5 py-3 rounded-2xl border border-slate-200 shadow-sm">
          <div class="w-2 h-2 rounded-full bg-[#20FCF1]"></div>
          <span class="text-[11px] font-black uppercase text-slate-600 tracking-tight">{{ factor }}</span>
        </div>
      </div>
    </section>

    <!-- 5. EVIDENCE VAULT -->
    <section v-if="submissionData.evidenceUrls?.length > 0" class="space-y-6">
      <div class="flex items-center gap-3 text-left">
        <div class="bg-[#0A1630] p-2 rounded-lg shadow-sm">
          <ArchiveBoxIcon class="h-5 w-5 text-[#20FCF1]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">3. Evidence Synthesis Vault</h3>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 p-10 bg-slate-900 rounded-[40px] shadow-2xl relative text-left">
        <div v-for="(file, idx) in submissionData.evidenceUrls" :key="idx" class="group relative aspect-square bg-white/5 border border-white/10 rounded-[32px] overflow-hidden hover:border-[#20FCF1] transition-all">
          <div v-if="isImage(file.url)" class="w-full h-full">
            <img :src="file.url" class="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
          </div>
          <div v-else class="w-full h-full flex flex-col items-center justify-center p-6 text-center">
            <DocumentIcon class="h-10 w-10 text-[#20FCF1] mb-3" />
            <span class="text-[8px] font-black text-white uppercase tracking-widest break-all">{{ file.name }}</span>
          </div>
          <a :href="file.url" target="_blank" class="absolute inset-0 bg-[#0A1630]/80 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center gap-3">
             <div class="bg-[#20FCF1] p-3 rounded-full text-[#0A1630] shadow-xl"><ArrowTopRightOnSquareIcon class="h-5 w-5" /></div>
             <span class="text-[10px] font-black text-white uppercase tracking-widest">Open Evidence</span>
          </a>
        </div>
      </div>
    </section>

    <!-- 6. FINDINGS & LESSONS -->
    <section class="grid grid-cols-1 lg:grid-cols-2 gap-10 text-left">
       <div class="bg-white border-2 border-slate-100 p-10 rounded-[40px] shadow-sm">
          <div class="flex items-center gap-3 mb-6">
            <div class="bg-[#0A1630] p-2 rounded-lg"><SparklesIcon class="h-5 w-5 text-[#20FCF1]" /></div>
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Lessons Learned Registry</h4>
          </div>
          <p class="text-sm text-slate-700 font-bold leading-relaxed whitespace-pre-wrap italic">"{{ submissionData.lessonsLearned }}"</p>
       </div>
       <div class="bg-[#EBFDF8] border-2 border-[#20FCF1]/30 p-10 rounded-[40px] shadow-sm">
          <div class="flex items-center gap-3 mb-6">
            <div class="bg-[#20FCF1] p-2 rounded-lg"><ShieldCheckIcon class="h-5 w-5 text-[#0A1630]" /></div>
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Corrective & Preventative Actions</h4>
          </div>
          <p class="text-sm text-[#0A1630] font-bold leading-relaxed whitespace-pre-wrap">{{ submissionData.recommendations }}</p>
       </div>
    </section>

    <!-- 7. SENTINEL INTELLIGENCE BRIEF -->
    <section id="executive-summary" class="animate-slideDown pt-12 text-left">
      <div class="bg-[#0A1630] p-12 md:p-16 rounded-[60px] text-white shadow-2xl relative overflow-hidden border-b-[12px] border-[#20FCF1]">
         <div class="absolute top-0 right-0 p-16 opacity-5 pointer-events-none">
            <ClipboardDocumentCheckIcon class="h-48 w-48 text-white" />
         </div>
         <div class="relative z-10">
            <div class="flex items-center gap-4 mb-12 border-l-4 border-[#20FCF1] pl-8">
               <div>
                  <h3 class="text-3xl font-black uppercase tracking-tighter leading-none text-left">Sentinel Intelligence</h3>
                  <p class="text-[9px] font-black text-[#20FCF1] uppercase tracking-[0.4em] mt-3">Automated Analytical Registry Brief</p>
               </div>
            </div>
            
            <div class="space-y-12 max-w-5xl">
               <!-- Primary Incident Brief -->
               <div class="p-10 bg-white/5 rounded-[40px] border border-white/10 backdrop-blur-sm">
                  <p class="text-lg text-slate-200 leading-relaxed font-medium italic">
                    On {{ submissionData.accidentDate }}, an incident involving {{ submissionData.subjectUser }} was identified at {{ submissionData.accidentLocation }}. 
                    Investigation analysis indicates the primary task context was {{ submissionData.taskAtTime || 'Unspecified' }}.
                  </p>
               </div>

               <!-- VECTOR ANALYSIS -->
               <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div class="space-y-6">
                    <h4 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] flex items-center gap-2">
                       <ScaleIcon class="h-4 w-4" /> 2.1 Contributory Evidence Summary
                    </h4>
                    <div class="space-y-4">
                       <p v-if="vectorBreakdown.human.length" class="text-xs text-slate-400 leading-relaxed font-bold uppercase">
                          <span class="text-white">Human Factors:</span> {{ vectorBreakdown.human.join(', ') }}.
                       </p>
                       <p v-if="vectorBreakdown.technical.length" class="text-xs text-slate-400 leading-relaxed font-bold uppercase">
                          <span class="text-white">Technical Integrity:</span> {{ vectorBreakdown.technical.join(', ') }}.
                       </p>
                       <p v-if="vectorBreakdown.systemic.length" class="text-xs text-slate-400 leading-relaxed font-bold uppercase">
                          <span class="text-white">Systemic Failures:</span> {{ vectorBreakdown.systemic.join(', ') }}.
                       </p>
                       <p v-if="submissionData.selectedFactors?.length === 0" class="text-xs text-slate-500 italic">No formal contributory vectors selected.</p>
                    </div>
                  </div>

                  <div class="space-y-6 border-l border-white/10 pl-10">
                    <h4 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] flex items-center gap-2">
                       <BoltIcon class="h-4 w-4" /> 2.2 Directives & Findings
                    </h4>
                    <div class="space-y-4">
                       <div>
                          <p class="text-[9px] font-black text-slate-500 uppercase mb-1">Root Conclusion</p>
                          <p class="text-xs text-slate-200 font-bold uppercase">{{ truncateText(submissionData.summary, 120) }}</p>
                       </div>
                       <div>
                          <p class="text-[9px] font-black text-slate-500 uppercase mb-1">Preventative Path</p>
                          <p class="text-xs text-slate-200 font-bold uppercase">{{ truncateText(submissionData.recommendations, 120) }}</p>
                       </div>
                    </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
    </section>

    <!-- 8. STAGE 2: MANAGEMENT APPRAISAL -->
    <section id="management-appraisal" v-if="hasAppraisalAccess || appraisalData.appraisedBy" class="pt-20 border-t-8 border-slate-100 animate-fadeIn text-left">
      <div class="flex items-center gap-4 mb-10">
        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg border-b-2 border-[#20FCF1]/30">
          <ShieldCheckIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">8. Management Appraisal & Close-Out</h2>
      </div>

      <div class="bg-[#0A1630] p-10 md:p-14 rounded-[50px] text-white shadow-2xl relative overflow-hidden border-b-[12px] border-[#20FCF1]">
        <div class="absolute top-0 right-0 p-12 opacity-5"><ShieldCheckIcon class="h-64 w-64" /></div>
        
        <div class="relative z-10">
          <div class="flex flex-col md:flex-row items-start md:items-center justify-between mb-12 gap-8 text-left">
            <div>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.4em] mb-4">Stage 2 Authoritative Review • Command Tier Only</p>
              <h2 class="text-3xl font-black uppercase tracking-tight leading-none text-left">Internal Compliance Appraisal</h2>
            </div>
            <div v-if="appraisalData.status" :class="statusClass(appraisalData.status)" class="px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl border border-white/10 backdrop-blur-md">
              {{ appraisalData.status }}
            </div>
          </div>

          <div class="space-y-8">
            <div>
               <label class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] ml-2 block mb-4">Director Appraisal Notes & Further Action Requirements</label>
               <textarea 
                 v-model="appraisalData.notes" 
                 :readonly="!hasAppraisalAccess" 
                 rows="6" 
                 class="w-full bg-white/5 border border-white/10 rounded-[40px] p-10 text-base font-medium text-slate-200 focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder:text-white/10" 
                 placeholder="Enter managerial findings, request for information, or final close-out directives..."
               ></textarea>
            </div>

            <!-- AUDIT SEAL -->
            <div v-if="appraisalData.appraisedBy" class="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-8 animate-fadeIn text-left">
               <div class="flex items-center gap-6">
                  <div class="h-16 w-16 bg-white p-4 rounded-2xl flex items-center justify-center shadow-lg"><CheckCircleIcon class="h-8 w-8 text-[#20FCF1]" /></div>
                  <div>
                     <p class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-1">Authoritative Close-Out Seal</p>
                     <p class="text-xl font-black text-white uppercase tracking-tight leading-none text-left">{{ appraisalData.appraisedBy }}</p>
                     <p class="text-[9px] font-bold text-slate-500 uppercase mt-2 tracking-widest">Handshake Verified: {{ formatDate(appraisalData.appraisedAt) }}</p>
                  </div>
               </div>
               
               <div v-if="hasAppraisalAccess" class="flex flex-wrap justify-center gap-3">
                 <button v-for="status in ['In Progress', 'Requires Action', 'Closed/Resolved']" :key="status" @click="appraisalData.status = status" :class="appraisalData.status === status ? 'bg-[#20FCF1] text-[#0A1630] scale-105' : 'bg-white/5 text-white'" class="px-6 py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">{{ status }}</button>
               </div>
            </div>

            <div v-if="hasAppraisalAccess" class="mt-8 flex justify-end">
               <button 
                 @click="saveAppraisal" 
                 :disabled="isSavingAppraisal" 
                 class="bg-[#20FCF1] text-[#0A1630] px-12 py-5 rounded-[24px] font-black uppercase tracking-[0.2em] text-xs shadow-2xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
               >
                 <span v-if="isSavingAppraisal" class="flex items-center gap-2">
                   <div class="animate-spin h-4 w-4 border-2 border-[#0A1630] border-t-transparent rounded-full"></div>
                   Syncing...
                 </span>
                 <span v-else>Apply Management Seal</span>
               </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 9. ORIGINAL SUBMISSION SEAL -->
    <section class="border-t-2 border-slate-100 pt-16 flex flex-col md:flex-row justify-between items-end gap-10 text-left">
        <div class="space-y-6">
          <div class="flex items-center gap-3">
            <CheckCircleIcon class="h-6 w-6 text-[#20FCF1]" />
            <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Investigation Registry Verified</p>
          </div>
          <div class="p-8 bg-slate-900 rounded-[32px] border-l-8 border-[#20FCF1] max-w-md shadow-2xl">
            <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 text-left">Authorised Investigating Official</p>
            <p class="text-xl font-black text-white uppercase tracking-tight leading-none mb-4 text-left">{{ submissionData.adminName || 'System Admin' }}</p>
            <div class="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-xl border border-white/10 w-fit">
              <div class="w-1.5 h-1.5 rounded-full bg-[#20FCF1] animate-pulse"></div>
              <span class="text-[9px] font-black uppercase text-[#20FCF1] tracking-widest">Validation Seal Applied</span>
            </div>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl" class="bg-white p-8 rounded-[48px] border border-slate-200 shadow-2xl flex flex-col items-center min-w-[320px]">
          <p class="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 mb-6">Security Handshake Seal</p>
          <img :src="submissionData.signatureDataUrl" alt="Admin Seal" class="max-h-24 object-contain mb-6 grayscale" />
          <div class="w-full h-px bg-slate-100 mb-6"></div>
          <div class="flex flex-col items-center gap-1">
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">{{ formatDate(submissionData.submittedAt) }}</span>
            <span class="text-[8px] font-bold text-slate-400 uppercase tracking-tighter italic">Permanent Hash: {{ submissionData.humanReadableId }}</span>
          </div>
        </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import { 
  LinkIcon, AdjustmentsHorizontalIcon, 
  ShieldCheckIcon, ArchiveBoxIcon, CheckCircleIcon, DocumentIcon,
  ArrowDownCircleIcon, ArrowTopRightOnSquareIcon, SparklesIcon,
  ClipboardDocumentCheckIcon, ScaleIcon, BoltIcon, ChatBubbleLeftRightIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  formatBoolean: { type: Function, required: true }
});

const userStore = useUserStore();
const isSavingAppraisal = ref(false);

const narrativeFields = [
  { key: 'taskAtTime', label: 'Task Context' },
  { key: 'sequenceOfEvents', label: 'Sequence of Events' },
  { key: 'postIncidentOutcome', label: 'Immediate Outcome' }
];

const appraisalData = reactive({
  notes: '',
  status: 'Pending Review',
  appraisedBy: '',
  appraisedAt: null
});

const hasAppraisalAccess = computed(() => {
  return userStore.isAdmin || userStore.isDirector || userStore.isHSManager;
});

const vectorBreakdown = computed(() => {
    const factors = props.submissionData.selectedFactors || [];
    return {
        human: factors.filter(f => f.toLowerCase().includes('behaviour') || f.toLowerCase().includes('human')).map(f => f.split(':').pop().trim()),
        technical: factors.filter(f => f.toLowerCase().includes('technical') || f.toLowerCase().includes('equipment')).map(f => f.split(':').pop().trim()),
        systemic: factors.filter(f => f.toLowerCase().includes('systemic') || f.toLowerCase().includes('management')).map(f => f.split(':').pop().trim())
    };
});

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const isImage = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|webp|avif|gif)$/.test(url.toLowerCase().split('?')[0]);
};

const truncateText = (text, length) => {
    if (!text) return 'Not Provided';
    return text.length > length ? text.substring(0, length) + '...' : text;
};

const statusClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (s.includes('CLOSED')) return 'bg-emerald-100 text-emerald-800';
  if (s.includes('ACTION')) return 'bg-rose-500 text-white';
  return 'bg-amber-500 text-[#0A1630]';
};

const saveAppraisal = async () => {
    if (!hasAppraisalAccess.value || isSavingAppraisal.value) return;
    isSavingAppraisal.value = true;
    try {
        const docRef = doc(db, 'healthsafetyFormSubmissions', props.submissionData.id);
        const adminName = `${userStore.userProfile.firstName} ${userStore.userProfile.lastName}`;
        const timestamp = new Date().toISOString();
        
        const updateObj = {
            appraisalStage2: {
                notes: appraisalData.notes,
                status: appraisalData.status,
                appraisedBy: adminName,
                appraisedAt: timestamp
            },
            status: appraisalData.status 
        };

        await updateDoc(docRef, updateObj);
        appraisalData.appraisedBy = adminName;
        appraisalData.appraisedAt = timestamp;
        alert("Management Appraisal Registry Synchronised.");
    } catch (e) {
        alert("Registry sync failure: " + e.message);
    } finally {
        isSavingAppraisal.value = false;
    }
};

onMounted(() => {
    if (props.submissionData.appraisalStage2) {
        Object.assign(appraisalData, props.submissionData.appraisalStage2);
    }
});
</script>

<style scoped>
.StatCard { @apply flex flex-col p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate tracking-tight; }
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

.animate-slideDown { animation: slideDown 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes slideDown { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
</style>